import { useMemo, useState } from 'react'
import { Field, NumberInput, ResultStat, Slider } from './ui'

function MosfetThermal() {
  const [power, setPower] = useState(5)
  const [caseTemp, setCaseTemp] = useState(60)
  const [thetaJC, setThetaJC] = useState(1.5)
  const [thetaCA, setThetaCA] = useState(8)

  const result = useMemo(() => {
    const riseJC = power * thetaJC
    const tj = caseTemp + riseJC
    const tjAmbient = 25 + power * (thetaJC + thetaCA)
    let status: 'safe' | 'warning' | 'critical'
    if (tj > 135) status = 'critical'
    else if (tj >= 105) status = 'warning'
    else status = 'safe'
    return { riseJC, tj, tjAmbient, status }
  }, [power, caseTemp, thetaJC, thetaCA])

  const statusMeta = {
    safe: { label: 'Safe Operating Temperature', bar: 'bg-emerald-500', text: 'text-emerald-600', pct: Math.min(100, (result.tj / 135) * 100) },
    warning: { label: 'Warning — Thermal Throttling / Degraded Lifespan', bar: 'bg-amber-500', text: 'text-amber-600', pct: Math.min(100, (result.tj / 135) * 100) },
    critical: { label: 'Critical Danger — Meltdown Risk', bar: 'bg-rose-500', text: 'text-rose-600', pct: 100 },
  }[result.status]

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="space-y-5">
        <Field label="Power Dissipation (P_D)" hint="Watts">
          <NumberInput value={power} onChange={setPower} min={0} max={500} step={0.1} />
        </Field>
        <Field label="Case Temperature (T_C)" hint="°C">
          <Slider value={caseTemp} onChange={setCaseTemp} min={-20} max={150} unit=" °C" />
        </Field>
        <Field label="Junction-to-Case θ_JC" hint="°C/W">
          <NumberInput value={thetaJC} onChange={setThetaJC} min={0} max={50} step={0.1} />
        </Field>
        <Field label="Case-to-Ambient θ_CA" hint="°C/W (heatsink)">
          <NumberInput value={thetaCA} onChange={setThetaCA} min={0} max={200} step={0.1} />
        </Field>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <ResultStat label="Junction Temp (T_J)" value={`${result.tj.toFixed(1)}`} sub="°C" accent={statusMeta.text} />
          <ResultStat label="Thermal Rise ΔT_JC" value={`${result.riseJC.toFixed(1)}`} sub="°C" />
        </div>
        <ResultStat label="Ambient-Air Forecast (25°C + heatsink)" value={`${result.tjAmbient.toFixed(1)} °C`} />
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Thermal SOA</span>
            <span className={`text-xs font-bold ${statusMeta.text}`}>{statusMeta.label}</span>
          </div>
          <div className="h-3 w-full overflow-hidden rounded-full bg-slate-100">
            <div className={`h-full rounded-full transition-all ${statusMeta.bar}`} style={{ width: `${statusMeta.pct}%` }} />
          </div>
          <div className="mt-1 flex justify-between font-mono text-[10px] text-slate-400">
            <span>0°C</span>
            <span>105°C</span>
            <span>135°C</span>
          </div>
        </div>
        <p className="text-xs leading-relaxed text-slate-500">
          <span className="font-mono text-slate-700">T_J = T_C + (P_D × θ_JC)</span>. SOA thresholds: ≥135°C critical,
          105–135°C warning, &lt;105°C safe.
        </p>
      </div>
    </div>
  )
}

export default MosfetThermal
